import React, { useMemo, useCallback } from 'react'
import CartReviewTile, { LoyaltyPointsEarningConfiguration } from './CartReviewTile'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import CartReviewPriceStack from './CartReviewPriceStack'
import CartReviewBanner from './CartReviewBanner'
import { getBannerTypeVariant } from './CartReviewUtils'
import useUserCartBedbankHotelItemData from 'hooks/UserCart/useUserCartBedbankHotelItemData'
import offerPageURL from 'lib/offer/offerPageURL'
import CartReviewTileImageContent from './CartReviewTileImageContent'
import { userCartItemsSaveForLater, userCartItemsDelete } from 'actions/AuthActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { getUserCartAccommodationItems } from 'selectors/userCart'
import { isBedbankItem } from 'lib/checkout/checkoutUtils'
import { queryKeyCheckIn, queryKeyCheckOut, queryKeyAdults, queryKeyChildren } from 'constants/url'
import { encodeChildrenOccupancy } from 'lib/search/searchUtils'
import { multiCartDeleteItemEvent, multiCartSaveItemForLaterEvent } from 'analytics/snowplow/events'
import { generateLoyaltyPointsCalculatorBedbankOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import AccommodationTileComparisonCheckbox from './AccommodationTileComparisonCheckbox'

interface Props {
  item: App.Checkout.BedbankHotelItem;
  className?: string
  hideTitleAndDates?: boolean
  hideAvailabilityDetails?: boolean
  disableOverlay?: boolean
  saveForLaterIfUnavailable?: boolean
  tileVariant?: 'default' | 'condensed'
}

function BedbankCartReviewTile({ item, className, hideTitleAndDates = false, hideAvailabilityDetails = false, disableOverlay = false, saveForLaterIfUnavailable = true, tileVariant = 'default' }: Props) {
  const dispatch = useAppDispatch()
  const data = useUserCartBedbankHotelItemData(item, saveForLaterIfUnavailable)

  const hotelItems = useAppSelector(getUserCartAccommodationItems)

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartSaveItemForLaterEvent(item, data.isSavedForLater)
    dispatch(userCartItemsSaveForLater([item.itemId]))
  }, [dispatch, item, data.isSavedForLater])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartDeleteItemEvent(item)
    dispatch(userCartItemsDelete([item.itemId]))
  }, [dispatch, item])

  const formattedDateRange = useMemo(() => {
    if (!item.checkIn || !item.checkOut) return false
    return `${formatDynamicDateRange(item.checkIn, item.checkOut)} · ${item.duration} nights`
  }, [item.checkIn, item.checkOut, item.duration])

  const formattedOccupancy = useMemo(() => {
    if (!item.occupancy) return false
    return formatOccupantsShort(item.occupancy)
  }, [item.occupancy])

  const {
    publicTotal,
    memberTotal,
  } = useMemo(() => getUserCartItemDisplayTotals(data.itemTotals), [data?.itemTotals])

  const bannerTypeVariant = getBannerTypeVariant(data.isPast, data.isSoldOut, false, !!data.error)

  const offerLink = useMemo(() => {
    const params = new URLSearchParams()

    const itemGroup = hotelItems.filter(isBedbankItem).filter(i => i.offerId === item.offerId && i.checkIn === item.checkIn && i.checkOut === item.checkOut)
    const rooms = itemGroup.map(i => i.occupancy)

    if (!data.isPast) {
      params.set(queryKeyCheckIn, item.checkIn)
      params.set(queryKeyCheckOut, item.checkOut)
    }

    params.set(queryKeyAdults, rooms.map(room => room.adults).join(','))
    params.set(queryKeyChildren, rooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(','))

    return offerPageURL(data.offer, params)
  }, [data.offer, hotelItems, item, data.isPast])

  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    if (!data.itemTotals || !data.offer || !data.rate) {
      return {
        calculationRequests: [
        ],
        calculationType: 'exact',
      }
    }
    return {
      calculationRequests: [
        generateLoyaltyPointsCalculatorBedbankOptions(data.rate, {
          price: publicTotal,
          memberPrice: memberTotal,
          propertyFees: data.itemTotals.propertiesFees?.reduce((acc, fee) => acc + fee.propertyFees, 0) ?? 0,
        }),
      ],
      calculationType: 'exact',
    }
  }, [data.itemTotals, data.offer, data.rate, publicTotal, memberTotal])

  const priceTotals = useMemo(() => ({
    price: publicTotal,
    memberPrice: memberTotal,
    value: data.itemTotals?.value ?? 0,
  }), [data.itemTotals?.value, memberTotal, publicTotal])

  const filters = useMemo(() => ({
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    rooms: [item.occupancy],
    roomRateId: item.roomRateId,
  }), [item.checkIn, item.checkOut, item.occupancy, item.roomRateId])

  return (
    <CartReviewTile
      itemId={item.itemId}
      fetching={data.loading}
      imageElement={<CartReviewTileImageContent image={data.package?.images[0] || data.offer?.image} />}
      to={offerLink}
      title={data.offer?.property.name || data.offer?.name || ''}
      dates={formattedDateRange}
      packageTitle={data.package?.name}
      occupancy={formattedOccupancy}
      price={<CartReviewPriceStack
        price={publicTotal}
        memberPrice={memberTotal}
        value={data.itemTotals?.value ?? 0}
        luxPlusOfferConfig={data.offer?.luxPlus || {}}
      />}
      banner={<CartReviewBanner
        variant={bannerTypeVariant}
        link={offerLink}
      />}
      loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
      isBookable={data.isBookable}
      onSaveForLater={handleSaveForLater}
      saveForLater={data.isSavedForLater}
      deleted={data.isDeleted}
      onDelete={handleDelete}
      className={className}
      hideAvailabilityDetails={hideAvailabilityDetails}
      hideTitleAndDates={hideTitleAndDates}
      disableOverlay={disableOverlay}
      tileVariant={tileVariant}
      comparisonCheckbox={<AccommodationTileComparisonCheckbox
        offerType="bedbank_hotel"
        offerId={data.offer?.id || ''}
        packageId={data.package?.roomId}
        name={data.offer?.name || ''}
        filters={filters}
        priceTotals={priceTotals}
      />}
    />
  )
}

export default BedbankCartReviewTile
